import React, { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import Icon from '../components/Icon';
import { networkMetadata, formatCurrency } from '../utils/format';

const TYPE_META = {
  treasury:      { icon: 'shield',   label: 'Treasury',     accent: 'var(--teal)' },
  paymaster:     { icon: 'settings', label: 'Paymaster',    accent: 'var(--info)' },
  rounds_pool:   { icon: 'wallet',   label: 'Rounds Pool',  accent: 'var(--warn)' },
  harambee_pool: { icon: 'wallet',   label: 'Harambee Pool', accent: 'var(--ok)' },
};

const formatNative = (n) => {
  const v = parseFloat(n) || 0;
  if (v < 0.01) return v.toFixed(6);
  if (v < 1) return v.toFixed(4);
  return v.toFixed(2);
};

const Wallets = () => {
  const { data, environment, loading } = useOutletContext();
  const wallets = data.wallets || [];

  const totals = useMemo(() => {
    return wallets.reduce(
      (acc, w) => {
        const usdc = parseFloat(w.usdc_balance) || 0;
        acc.usdc += usdc;
        acc.alerts += w.low_balance_threshold && usdc < parseFloat(w.low_balance_threshold) ? 1 : 0;
        const nb = acc.byNetwork[w.network] || { usdc: 0, count: 0 };
        nb.usdc += usdc;
        nb.count += 1;
        acc.byNetwork[w.network] = nb;
        return acc;
      },
      { usdc: 0, alerts: 0, byNetwork: {} }
    );
  }, [wallets]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Wallets</h1>
          <p>
            Treasury, paymaster, and pool balances across all chains
            <span className="text-tx3"> · {environment}</span>
          </p>
        </div>
      </div>

      <div className="dashboard-grid stagger mb-4">
        <div className="card">
          <div className="card-title">Total USDC Held</div>
          <div className="stat-value" style={{ color: 'var(--teal)' }}>
            {formatCurrency(totals.usdc, { compact: true })}
          </div>
          <div className="stat-label">Across {wallets.length} wallets</div>
        </div>
        <div className="card">
          <div className="card-title">Active Alerts</div>
          <div className="stat-value" style={{ color: totals.alerts > 0 ? 'var(--err)' : 'var(--ok)' }}>
            {totals.alerts}
          </div>
          <div className="stat-label">Wallets below threshold</div>
        </div>
        <div className="card">
          <div className="card-title">Networks</div>
          <div className="stat-value">{Object.keys(totals.byNetwork).length}</div>
          <div className="stat-label">Active chains</div>
        </div>
      </div>

      {Object.keys(totals.byNetwork).length > 0 && (
        <div className="card mb-4">
          <div className="card-title">Chain Breakdown</div>
          <div className="row row-gap-3" style={{ flexWrap: 'wrap' }}>
            {Object.entries(totals.byNetwork).map(([net, info]) => (
              <div key={net} className="wallet-card" style={{ flex: '1 1 200px', minWidth: 180 }}>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx1)' }}>
                    {networkMetadata[net]?.name || net}
                  </span>
                  <span className="badge badge-info">{info.count} wallet{info.count !== 1 ? 's' : ''}</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--teal)' }}>
                  {formatCurrency(info.usdc, { compact: true })}
                </div>
                <div className="text-tx3 text-xs">USDC balance</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-title mb-3">All Wallets</div>
      {wallets.length === 0 ? (
        <div className="card">
          <div className="empty">
            <div className="empty-icon"><Icon name="wallet" size={26} /></div>
            <h3>{loading ? 'Loading wallets…' : 'No wallets configured'}</h3>
            <p>{loading ? 'Pulling latest balances from Supabase.' : `No wallets exist in ${environment}. Seed via the wallets table or switch environment.`}</p>
          </div>
        </div>
      ) : (
        <div className="dashboard-grid stagger">
          {wallets.map((wallet) => {
            const meta = TYPE_META[wallet.type] || TYPE_META.treasury;
            const usdc = parseFloat(wallet.usdc_balance) || 0;
            const isLow = wallet.low_balance_threshold && usdc < parseFloat(wallet.low_balance_threshold);
            return (
              <div
                key={wallet.id}
                className="card"
                style={isLow ? { borderColor: 'var(--err)', borderWidth: 1 } : undefined}
              >
                <div className="row mb-3" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className="row row-gap-3" style={{ alignItems: 'center' }}>
                    <div
                      style={{
                        width: 40, height: 40,
                        borderRadius: 'var(--r-md)',
                        background: 'var(--bg-elevated)',
                        display: 'grid', placeItems: 'center',
                        color: meta.accent,
                      }}
                    >
                      <Icon name={meta.icon} size={20} />
                    </div>
                    <div className="col col-gap-2">
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tx1)' }}>{wallet.label}</div>
                      <div className="text-tx3 text-xs" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {meta.label} · {wallet.network}
                      </div>
                    </div>
                  </div>
                  {isLow && <span className="badge badge-danger">LOW</span>}
                </div>

                <div className="row row-gap-4" style={{ alignItems: 'flex-end' }}>
                  <div className="col col-gap-2 flex-1">
                    <div className="text-tx3 text-xs">USDC Balance</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--teal)', lineHeight: 1.05 }}>
                      {formatCurrency(usdc, { compact: false })}
                    </div>
                  </div>
                  <div className="col col-gap-2 flex-1">
                    <div className="text-tx3 text-xs">Native</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--tx1)' }}>
                      {formatNative(wallet.native_balance)}
                    </div>
                  </div>
                </div>

                {wallet.type === 'treasury' && wallet.safe_threshold && (
                  <>
                    <div className="card-divider" />
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="text-tx3 text-xs">Safe signers</span>
                      <span className="text-sm" style={{ color: 'var(--tx1)', fontWeight: 600 }}>
                        {wallet.safe_signers?.length || 0} / threshold {wallet.safe_threshold}
                      </span>
                    </div>
                  </>
                )}

                {(wallet.type === 'rounds_pool' || wallet.type === 'harambee_pool') && (
                  <>
                    <div className="card-divider" />
                    <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
                      <span className="text-tx3 text-xs">TVL</span>
                      <span className="text-sm" style={{ color: 'var(--tx1)', fontWeight: 600 }}>
                        {formatCurrency(wallet.total_value_locked, { compact: true })}
                      </span>
                    </div>
                    <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
                      <span className="text-tx3 text-xs">Participants</span>
                      <span className="text-sm" style={{ color: 'var(--tx1)', fontWeight: 600 }}>
                        {wallet.active_participants || 0}
                      </span>
                    </div>
                    {wallet.next_distribution_at && (
                      <div className="row" style={{ justifyContent: 'space-between' }}>
                        <span className="text-tx3 text-xs">Next distribution</span>
                        <span className="text-sm" style={{ color: 'var(--warn)', fontWeight: 600 }}>
                          {new Date(wallet.next_distribution_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default Wallets;
